import React, { useEffect, useState } from 'react';
import AnimatedProgressRing from '../visual/AnimatedProgressRing';

/**
 * BreathingExercise — a working paced-breathing guide, now with a large animated
 * ring that fills with the breath. The ring's arc grows over the inhale, holds,
 * and empties over the exhale (its transition duration is set to each phase's
 * length), while a soft orb scales in sync. Pure state + SVG/CSS, no dependencies;
 * the timer and the visuals share one source of truth so they never drift.
 */
const PHASES = [
  { key: 'inhale', label: 'Breathe in', secs: 4, value: 1, scale: 1 },
  { key: 'hold', label: 'Hold', secs: 4, value: 1, scale: 1 },
  { key: 'exhale', label: 'Breathe out', secs: 6, value: 0, scale: 0.6 },
] as const;

export const BreathingExercise: React.FC = () => {
  // Starts breathing on its own so the orb is visibly animating the moment the
  // card opens (no click needed); Pause/End remain available.
  const [running, setRunning] = useState(true);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [cycles, setCycles] = useState(0);

  useEffect(() => {
    if (!running) return;
    const t = setTimeout(() => {
      setPhaseIdx((i) => {
        const next = (i + 1) % PHASES.length;
        if (next === 0) setCycles((c) => c + 1);
        return next;
      });
    }, PHASES[phaseIdx].secs * 1000);
    return () => clearTimeout(t);
  }, [running, phaseIdx]);

  const phase = PHASES[phaseIdx];
  const ringValue = running ? phase.value : 0;
  const ringDuration = running ? (phase.key === 'hold' ? 300 : phase.secs * 1000) : 600;
  const orbScale = running ? phase.scale : 0.6;

  const end = () => {
    setRunning(false);
    setPhaseIdx(0);
    setCycles(0);
  };

  return (
    <div className="mm-breathe">
      <AnimatedProgressRing
        value={ringValue}
        durationMs={ringDuration}
        easing="ease-in-out"
        size={212}
        stroke={12}
        label={running ? phase.label : 'Breathing exercise'}
      >
        <div className="mm-breathe__inner">
          <span
            className="mm-breathe__orb"
            style={{ transform: `scale(${orbScale})`, transitionDuration: `${ringDuration}ms` }}
          />
          <span className="mm-breathe__phase">{running ? phase.label : 'Ready'}</span>
          <span className="mm-breathe__count">{cycles} {cycles === 1 ? 'breath' : 'breaths'}</span>
        </div>
      </AnimatedProgressRing>

      <div className="mm-activity__controls">
        <button type="button" className="mm-btn mm-btn--primary" onClick={() => setRunning((r) => !r)}>
          {running ? 'Pause' : cycles > 0 ? 'Resume' : 'Begin'}
        </button>
        <button type="button" className="mm-btn mm-btn--ghost" onClick={end} disabled={!running && cycles === 0}>
          End
        </button>
      </div>
    </div>
  );
};

export default BreathingExercise;
